import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Star, ShoppingCart, Heart, Share2, Play, X, ChevronLeft, ChevronRight, Minus, Plus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import PurchaseModal from '../components/PurchaseModal';
import PincodeChecker from '../components/PincodeChecker';
import SEO from '../components/SEO';
import { getProduct, getProductMedia, listProducts } from '../api/products';
import { Product, ColorVariant } from '../data/products';
import ProductCard from '../components/ProductCard';

interface LazyVideoProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  src: string;
}

const LazyVideo = React.forwardRef<HTMLVideoElement, LazyVideoProps>(({ src, preload: _ignoredPreload, ...rest }, forwardedRef) => {
  const innerRef = useRef<HTMLVideoElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  const setRefs = useCallback(
    (node: HTMLVideoElement | null) => {
      innerRef.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef]
  );

  useEffect(() => {
    const videoEl = innerRef.current;
    if (!videoEl) return;

    if (typeof window === 'undefined') {
      setShouldLoad(true);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '200px', threshold: 0.1 }
    );

    observer.observe(videoEl);

    return () => {
      observer.disconnect();
    };
  }, [src]);

  useEffect(() => {
    const videoEl = innerRef.current;
    if (!videoEl || !shouldLoad) return;

    if (!videoEl.src) {
      videoEl.src = src;
      videoEl.load();
    }
  }, [shouldLoad, src]);

  return <video ref={setRefs} preload="none" {...rest} />;
});

LazyVideo.displayName = 'LazyVideo';

const normalizeFeatureInput = (input: unknown): string[] => {
  if (!input) return [];

  if (Array.isArray(input)) {
    return input.flatMap((item) => normalizeFeatureInput(item));
  }

  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) {
      return [];
    }

    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      try {
        const parsed = JSON.parse(trimmed);
        return normalizeFeatureInput(parsed);
      } catch {
        // fall through to split-based parsing
      }
    }

    return trimmed
      .split(/[\n\r,]+/)
      .map((feature) => feature.trim())
      .filter(Boolean);
  }

  if (typeof input === 'object') {
    const values = Object.values(input as Record<string, unknown>);
    return values.flatMap((value) => normalizeFeatureInput(value));
  }

  return [];
};

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state, addToCart, updateQuantity } = useCart();
  const [selectedImage, setSelectedImage] = useState(0);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [media, setMedia] = useState<{ images: string[]; banners: string[]; videos: string[] }>({ images: [], banners: [], videos: [] });
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const thumbnailScrollRef = useRef<HTMLDivElement>(null);
  const mainImageScrollRef = useRef<HTMLDivElement>(null);
  const relatedScrollRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<{ [key: number | string]: HTMLVideoElement | null }>({});
  const [, setCurrentThumbnailIndex] = useState(0);

  useEffect(() => {
    if (!id) return;
    // Scroll to top when product page loads
    window.scrollTo(0, 0);

    setLoading(true);
    getProduct(id)
      .then(setProduct)
      .finally(() => setLoading(false));

    getProductMedia(id).then(setMedia).catch(() => { }); // Fetch media, ignore error
  }, [id]);

  useEffect(() => {
    if (!product?.category) return;

    listProducts({ category: product.category, limit: 12 })
      .then((res) => {
        const filtered = (res.items || []).filter(
          (p) => String(p.id) !== String(product.id) && p.slug !== product.slug
        );
        setRelatedProducts(filtered);
      })
      .catch((err) => {
        console.error('Failed to load related products:', err);
      });
  }, [product?.category, product?.id, product?.slug]);

  const scrollRelated = (direction: 'left' | 'right') => {
    if (relatedScrollRef.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      relatedScrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Video URL handler (define before early returns)
  const getVideoEmbedUrl = (url: string): string | null => {
    if (!url) return null;

    // YouTube
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }

    // Vimeo
    if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
    }

    // Direct video URL
    if (url.match(/\.(mp4|webm|mov)$/i)) {
      return url;
    }

    return null;
  };

  const [selectedColorVariant, setSelectedColorVariant] = useState<ColorVariant | null>(null);

  useEffect(() => {
    if (product?.colorVariants && product.colorVariants.length > 0) {
      const defaultVar = product.colorVariants.find(v => v.isDefault) || product.colorVariants[0];
      setSelectedColorVariant(defaultVar);
    } else {
      setSelectedColorVariant(null);
    }
  }, [product]);

  // Combine main image with media images or color variant images
  const productImages = useMemo(() => {
    if (!product) return [];
    if (selectedColorVariant) {
      const variantImages = [
        ...(selectedColorVariant.image ? [selectedColorVariant.image] : []),
        ...(selectedColorVariant.images || [])
      ].filter(Boolean);
      if (variantImages.length > 0) {
        return variantImages;
      }
    }
    return [
      ...(product.image ? [product.image] : []),
      ...(media.images || [])
    ].filter(Boolean);
  }, [product, media.images, selectedColorVariant]);

  const videoEmbedUrl = product?.videoUrl ? getVideoEmbedUrl(product.videoUrl) : null;
  const hasVideoFiles = media.videos && media.videos.length > 0;

  // Calculate total thumbnails for display logic
  const totalThumbnails = useMemo(() => {
    return productImages.length +
      (hasVideoFiles ? (media.videos?.length || 0) : 0) +
      (videoEmbedUrl ? 1 : 0);
  }, [productImages.length, hasVideoFiles, media.videos?.length, videoEmbedUrl]);

  // Auto-scroll thumbnail slider every 3 seconds (must be before early returns)
  useEffect(() => {
    if (!product) return;

    if (totalThumbnails <= 1) return; // Don't auto-scroll if only one thumbnail

    const interval = setInterval(() => {
      setCurrentThumbnailIndex((prev) => {
        const nextIndex = (prev + 1) % totalThumbnails;

        // Scroll thumbnail slider to show the next thumbnail
        if (thumbnailScrollRef.current) {
          const thumbnailWidth = 80 + 12; // w-20 (80px) + gap-3 (12px)
          const scrollAmount = nextIndex * thumbnailWidth;
          thumbnailScrollRef.current.scrollTo({ left: scrollAmount, behavior: 'smooth' });
        }

        // Also update main image if it's an image thumbnail
        if (nextIndex < productImages.length && mainImageScrollRef.current) {
          setSelectedImage(nextIndex);
          const scrollAmount = mainImageScrollRef.current.offsetWidth * nextIndex;
          mainImageScrollRef.current.scrollTo({ left: scrollAmount, behavior: 'smooth' });
        }

        return nextIndex;
      });
    }, 3000); // 3 seconds

    return () => clearInterval(interval);
  }, [product, totalThumbnails, productImages.length]);

  const validFeatures = useMemo(() => normalizeFeatureInput(product?.features), [product?.features]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-600">Loading…</div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <p className="text-gray-600 mb-8">The product you're looking for doesn't exist.</p>
          <Link
            to="/products"
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Browse Products
          </Link>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product, selectedColorVariant?.name, selectedColorVariant || undefined);
  };

  const handleBuyNow = () => {
    setShowPurchaseModal(true);
  };

  const handlePurchase = (platform: 'amazon' | 'flipkart') => {
    const customLink = platform === 'amazon' ? selectedColorVariant?.amazonLink : selectedColorVariant?.flipkartLink;
    const url = customLink || (platform === 'amazon' ? product.amazonLink : product.flipkartLink);
    if (url) {
      window.open(url, '_blank');
    }
    setShowPurchaseModal(false);
  };

  const productSchema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": product.title,
    "image": productImages[0],
    "description": product.description,
    "sku": product.id,
    "brand": {
      "@type": "Brand",
      "name": "cnine"
    },
    "offers": {
      "@type": "Offer",
      "url": window.location.href,
      "priceCurrency": "INR",
      "price": product.price,
      "itemCondition": "https://schema.org/NewCondition",
      "availability": "https://schema.org/InStock"
    },
    "aggregateRating": product.rating ? {
      "@type": "AggregateRating",
      "ratingValue": product.rating,
      "reviewCount": product.reviews || 0
    } : undefined
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={product.title}
        description={product.shortDescription || product.description?.substring(0, 160)}
        ogImage={productImages[0]}
        ogType="product"
        structuredData={productSchema}
      />
      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-primary-600">Home</Link>
            <span>/</span>
            <Link to="/products" className="hover:text-primary-600">Products</Link>
            <span>/</span>
            <span className="text-gray-900">{product.title}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Product Section */}
        <div className={`grid grid-cols-1 ${totalThumbnails > 0 ? 'lg:grid-cols-2' : ''} gap-8 mb-4`}>
          {/* Image Gallery - Show if there are images, videos, or video URL */}
          {totalThumbnails > 0 ? (
            <div className="space-y-3">
              {/* Main Image - Scrollable with all media */}
              <div className="relative">
                <div
                  ref={mainImageScrollRef}
                  className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth aspect-square snap-x snap-mandatory"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {/* All Product Images */}
                  {productImages.map((image, index) => {
                    const fallbackImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
                    return (
                      <div
                        key={`main-img-${index}`}
                        className="flex-shrink-0 aspect-square w-full bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm snap-center"
                      >
                        <img
                          src={image || fallbackImage}
                          alt={`${product.title} ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            if (e.currentTarget.src !== fallbackImage) {
                              e.currentTarget.src = fallbackImage;
                            }
                          }}
                        />
                      </div>
                    );
                  })}

                  {/* Video Files */}
                  {hasVideoFiles && media.videos && media.videos.length > 0 && media.videos.map((video, index) => (
                    <div
                      key={`main-video-${index}`}
                      className="flex-shrink-0 aspect-square w-full bg-gray-900 rounded-xl overflow-hidden border border-gray-200 shadow-sm relative group snap-center"
                    >
                      <LazyVideo
                        src={video}
                        className="w-full h-full object-cover"
                        muted
                        playsInline
                        onMouseEnter={async (e) => {
                          try {
                            const playPromise = e.currentTarget.play();
                            if (playPromise) {
                              await playPromise;
                            }
                          } catch (error) {
                            // Ignore play errors
                          }
                        }}
                        onMouseLeave={async (e) => {
                          try {
                            e.currentTarget.pause();
                            e.currentTarget.currentTime = 0;
                          } catch (error) {
                            // Ignore pause errors
                          }
                        }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-40 transition-colors pointer-events-none">
                        <Play className="w-12 h-12 text-white" />
                      </div>
                      <button
                        onClick={() => {
                          setSelectedVideo(video);
                          setShowVideoModal(true);
                        }}
                        className="absolute inset-0 flex items-center justify-center bg-transparent hover:bg-black hover:bg-opacity-10 transition-colors cursor-pointer"
                      />
                    </div>
                  ))}

                  {/* Video URL Embed */}
                  {videoEmbedUrl && !hasVideoFiles && (
                    <div className="flex-shrink-0 aspect-square w-full bg-black rounded-xl overflow-hidden border border-gray-200 shadow-sm snap-center">
                      {videoEmbedUrl.includes('youtube.com') || videoEmbedUrl.includes('youtu.be') || videoEmbedUrl.includes('vimeo.com') ? (
                        <iframe
                          src={videoEmbedUrl}
                          className="w-full h-full object-cover"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={`Product Video - ${product.title}`}
                        ></iframe>
                      ) : (
                        <LazyVideo
                          src={videoEmbedUrl}
                          controls
                          className="w-full h-full"
                          playsInline
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* Main Image Navigation Buttons */}
                {(productImages.length + (hasVideoFiles ? (media.videos?.length || 0) : 0) + (videoEmbedUrl ? 1 : 0)) > 1 && (
                  <>
                    <button
                      onClick={() => {
                        if (mainImageScrollRef.current) {
                          mainImageScrollRef.current.scrollBy({ left: -mainImageScrollRef.current.offsetWidth, behavior: 'smooth' });
                        }
                      }}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white border border-gray-300 rounded-full p-2 shadow-lg z-10"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-700" />
                    </button>
                    <button
                      onClick={() => {
                        if (mainImageScrollRef.current) {
                          mainImageScrollRef.current.scrollBy({ left: mainImageScrollRef.current.offsetWidth, behavior: 'smooth' });
                        }
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white border border-gray-300 rounded-full p-2 shadow-lg z-10"
                    >
                      <ChevronRight className="w-5 h-5 text-gray-700" />
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnail Gallery - Images and Videos together with Slider */}
              {(productImages.length > 0 || (media.videos && media.videos.length > 0) || videoEmbedUrl) && (
                <div className="space-y-3">
                  {/* Combined Image and Video Thumbnails with Slider */}
                  <div className="relative">
                    <div
                      ref={thumbnailScrollRef}
                      className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth"
                      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                      {/* Image Thumbnails */}
                      {productImages.map((image, index) => {
                        const fallbackImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
                        return (
                          <button
                            key={`img-${index}`}
                            onClick={() => {
                              setSelectedImage(index);
                              // Scroll main image to selected item
                              if (mainImageScrollRef.current) {
                                const scrollAmount = mainImageScrollRef.current.offsetWidth * index;
                                mainImageScrollRef.current.scrollTo({ left: scrollAmount, behavior: 'smooth' });
                              }
                            }}
                            className={`flex-shrink-0 aspect-square w-20 bg-white rounded-lg overflow-hidden border-2 transition-all ${selectedImage === index
                              ? 'border-primary-600 ring-2 ring-primary-200'
                              : 'border-gray-200 hover:border-gray-300'
                              }`}
                          >
                            <img
                              src={image || fallbackImage}
                              alt={`${product.title} ${index + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                if (e.currentTarget.src !== fallbackImage) {
                                  e.currentTarget.src = fallbackImage;
                                }
                              }}
                            />
                          </button>
                        );
                      })}

                      {/* Video Thumbnails - in same slider */}
                      {media.videos && media.videos.length > 0 && media.videos.map((video, index) => {
                        const videoIndex = productImages.length + index;
                        return (
                          <button
                            key={`video-${index}`}
                            onClick={() => {
                              if (mainImageScrollRef.current) {
                                const scrollAmount = mainImageScrollRef.current.offsetWidth * videoIndex;
                                mainImageScrollRef.current.scrollTo({ left: scrollAmount, behavior: 'smooth' });
                              }
                              setSelectedVideo(video);
                              setShowVideoModal(true);
                            }}
                            className="flex-shrink-0 aspect-square w-20 relative rounded-lg overflow-hidden border-2 border-gray-200 hover:border-gray-300 bg-gradient-to-br from-gray-900 to-gray-700 group"
                          >
                            <div className="absolute inset-0 flex items-center justify-center text-white">
                              <Play className="w-6 h-6" />
                            </div>
                            <span className="sr-only">Play product video {index + 1}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Slider Navigation Buttons */}
                    {(productImages.length > 5 || hasVideoFiles) && (
                      <>
                        <button
                          onClick={() => {
                            if (thumbnailScrollRef.current) {
                              thumbnailScrollRef.current.scrollBy({ left: -100, behavior: 'smooth' });
                            }
                          }}
                          className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white border border-gray-300 rounded-full p-1.5 shadow-md z-10"
                        >
                          <ChevronLeft className="w-4 h-4 text-gray-700" />
                        </button>
                        <button
                          onClick={() => {
                            if (thumbnailScrollRef.current) {
                              thumbnailScrollRef.current.scrollBy({ left: 100, behavior: 'smooth' });
                            }
                          }}
                          className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white border border-gray-300 rounded-full p-1.5 shadow-md z-10"
                        >
                          <ChevronRight className="w-4 h-4 text-gray-700" />
                        </button>
                      </>
                    )}
                  </div>

                  {/* Video URL Embed (if no video files but has videoUrl) - Add to thumbnail slider */}
                  {videoEmbedUrl && !hasVideoFiles && (
                    <button
                      onClick={() => {
                        // Scroll main image to video URL embed
                        if (mainImageScrollRef.current) {
                          const scrollAmount = mainImageScrollRef.current.offsetWidth * productImages.length;
                          mainImageScrollRef.current.scrollTo({ left: scrollAmount, behavior: 'smooth' });
                        }
                      }}
                      className="flex-shrink-0 aspect-square w-20 relative rounded-lg overflow-hidden border-2 border-gray-200 hover:border-gray-300 bg-gray-100 group"
                    >
                      <div className="w-full h-full bg-black flex items-center justify-center">
                        <Play className="w-6 h-6 text-white" />
                      </div>
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : null}

          {/* Product Info */}
          <div className="space-y-6">
            {/* Title & Rating */}
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-3">{product.title}</h1>
              {product.shortDescription && (
                <p className="text-lg text-gray-600 mb-4">{product.shortDescription}</p>
              )}
              {product.rating && (
                <div className="flex items-center space-x-3">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${i < Math.floor(product.rating!)
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-300'
                          }`}
                      />
                    ))}
                  </div>
                  <span className="text-gray-600 font-medium">
                    {product.rating} ({product.reviews || 0} reviews)
                  </span>
                </div>
              )}
            </div>

            {/* Price */}
            {(() => {
              const displayPrice = selectedColorVariant?.price !== undefined ? selectedColorVariant.price : product.price;
              const displayOriginalPrice = selectedColorVariant?.originalPrice !== undefined ? selectedColorVariant.originalPrice : product.originalPrice;
              return (
                <div className="flex items-baseline space-x-4">
                  <span className="text-4xl font-bold text-gray-900">₹{displayPrice}</span>
                  {displayOriginalPrice && displayOriginalPrice > displayPrice && (
                    <>
                      <span className="text-2xl text-gray-500 line-through">₹{displayOriginalPrice}</span>
                      <span className="bg-red-100 text-red-700 text-sm font-bold px-3 py-1 rounded-full">
                        {Math.round(((displayOriginalPrice - displayPrice) / displayOriginalPrice) * 100)}% OFF
                      </span>
                    </>
                  )}
                </div>
              );
            })()}

            {/* Product Color Variants Selector */}
            {product.colorVariants && product.colorVariants.length > 0 && (
              <div className="space-y-3 pt-2 pb-2 border-t border-b border-gray-100">
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-bold text-gray-900">Choose your color :</span>
                  <span className="text-lg font-semibold text-gray-700">
                    {selectedColorVariant?.name || 'Select a color'}
                  </span>
                </div>

                <div className="flex flex-wrap gap-3 items-center">
                  {product.colorVariants.map((variant, index) => {
                    const isSelected = selectedColorVariant?.name === variant.name || selectedColorVariant?._id === variant._id;
                    const fallbackImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          setSelectedColorVariant(variant);
                          setSelectedImage(0);
                          if (mainImageScrollRef.current) {
                            mainImageScrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
                          }
                        }}
                        title={variant.name}
                        className={`group relative flex items-center justify-center w-16 h-20 p-1.5 rounded-xl border-2 transition-all cursor-pointer ${
                          isSelected
                            ? 'border-indigo-600 ring-2 ring-indigo-200 bg-indigo-50/20 shadow-md scale-105'
                            : 'border-gray-200 hover:border-gray-400 bg-white hover:shadow-sm'
                        }`}
                      >
                        {variant.image ? (
                          <img
                            src={variant.image}
                            alt={variant.name}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                            onError={(e) => {
                              if (e.currentTarget.src !== fallbackImage) {
                                e.currentTarget.src = fallbackImage;
                              }
                            }}
                          />
                        ) : (
                          <div
                            className="w-10 h-10 rounded-full border border-gray-300 shadow-inner"
                            style={{ backgroundColor: variant.colorCode || '#4554a4' }}
                          />
                        )}
                        
                        {/* Tooltip on hover */}
                        <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-gray-900 text-white text-[11px] font-medium rounded shadow opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20">
                          {variant.name}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-6 pt-4 border-t border-gray-200">
              <PincodeChecker />

              <div className="flex space-x-4">
                {(product.sellOnSite ?? true) ? (() => {
                  const cartItem = state.items.find(item => item.product.id === product.id && item.selectedColor === (selectedColorVariant?.name || undefined));
                  if (cartItem) {
                    return (
                      <>
                        <div className="flex-1 flex items-center justify-between bg-gray-100 py-2 px-4 rounded-xl shadow-lg border border-gray-200">
                          <div className="flex items-center space-x-6">
                            <button 
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); updateQuantity(product.id, cartItem.quantity - 1, cartItem.selectedColor); }}
                              className="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow hover:bg-gray-50 text-gray-700"
                            >
                              <Minus className="w-5 h-5" />
                            </button>
                            <span className="text-xl font-bold text-gray-900 w-8 text-center">{cartItem.quantity}</span>
                            <button 
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); updateQuantity(product.id, cartItem.quantity + 1, cartItem.selectedColor); }}
                              className="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow hover:bg-gray-50 text-gray-700"
                            >
                              <Plus className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate('/checkout')}
                          className="flex-1 text-gray-900 py-4 px-6 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-lg flex items-center justify-center"
                          style={{ backgroundColor: '#f8da0f' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#e6c80e';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8da0f';
                          }}
                        >
                          View Cart
                        </button>
                      </>
                    );
                  }

                  return (
                  <>
                    <button
                      onClick={handleAddToCart}
                      className="flex-1 text-white py-4 px-6 rounded-xl font-bold text-lg transition-all transform hover:scale-105 flex items-center justify-center shadow-lg"
                      style={{ backgroundColor: '#4554a4' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#374a8a';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#4554a4';
                      }}
                    >
                      <ShoppingCart className="w-6 h-6 mr-2" />
                      Add to Cart
                    </button>
                    <button
                      onClick={handleBuyNow}
                      className="flex-1 text-gray-900 py-4 px-6 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-lg"
                      style={{ backgroundColor: '#f8da0f' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e6c80e';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8da0f';
                      }}
                    >
                      Buy Now
                    </button>
                  </>
                  );
                })() : (
                  <>
                    {product.amazonLink && (
                      <button
                        onClick={() => window.open(product.amazonLink, '_blank')}
                        className="flex-1 text-white bg-gray-900 hover:bg-gray-800 py-4 px-6 rounded-xl font-bold text-lg transition-all transform hover:scale-105 flex items-center justify-center shadow-lg"
                      >
                        Buy on Amazon
                      </button>
                    )}
                    {product.flipkartLink && (
                      <button
                        onClick={() => window.open(product.flipkartLink, '_blank')}
                        className="flex-1 text-white bg-blue-600 hover:bg-blue-700 py-4 px-6 rounded-xl font-bold text-lg transition-all transform hover:scale-105 flex items-center justify-center shadow-lg"
                      >
                        Buy on Flipkart
                      </button>
                    )}
                    {(!product.amazonLink && !product.flipkartLink) && (
                      <div className="flex-1 text-center text-gray-500 py-4 px-6 rounded-xl border border-gray-200">
                        Currently Unavailable
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={() => setIsWishlisted(!isWishlisted)}
                  className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-colors flex items-center justify-center border-2 ${isWishlisted
                    ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                    }`}
                >
                  <Heart className={`w-5 h-5 mr-2 ${isWishlisted ? 'fill-current' : ''}`} />
                  {isWishlisted ? 'Wishlisted' : 'Add to Wishlist'}
                </button>
                <button className="flex-1 bg-white text-gray-700 border-2 border-gray-200 hover:bg-gray-50 py-3 px-6 rounded-xl font-semibold transition-colors flex items-center justify-center">
                  <Share2 className="w-5 h-5 mr-2" />
                  Share
                </button>
              </div>
            </div>

            {/* Highlights */}
            {product.highlights && product.highlights.length > 0 && product.highlights.filter(h => h && h.trim()).length > 0 && (
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Key Highlights</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {product.highlights.filter(h => h && h.trim()).map((highlight, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-primary-600 rounded-full flex-shrink-0"></div>
                      <span className="text-gray-700 font-semibold">{highlight}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}


            {/* Description */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">About This Product</h3>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{product.description}</p>
            </div>

            {/* Features */}
            {validFeatures.length > 0 && (
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Key Features</h3>
                <ul className="space-y-3">
                  {validFeatures.map((feature: string, index: number) => (
                    <li key={index} className="flex items-start">
                      <span className="w-2 h-2 bg-primary-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Quick Navigation Links Before Banners - Only if sections exist */}
      {product.specifications && Object.keys(product.specifications).length > 0 && (
        <div className="bg-gray-100 border-t mt-4 w-full py-4">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => {
                  document.getElementById('specifications-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-6 py-2 bg-white hover:bg-primary-50 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:text-primary-600 transition-colors"
              >
                Specifications
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Banners & Videos - Full Width Page Landscape */}
      {(media.banners && media.banners.length > 0) || (media.videos && media.videos.length > 0) || videoEmbedUrl ? (
        <div className="bg-gray-50 w-full" style={{ margin: 0, padding: 0 }}>
          <div className="w-full" style={{ margin: 0, padding: 0, lineHeight: 0 }}>
            {hasVideoFiles && media.videos && media.videos.length > 0 && media.videos.map((video, index) => (
              <div key={`banner-video-${index}`} className="w-full bg-gray-50 py-12" style={{ display: 'block' }}>
                <div className="relative w-full max-w-6xl mx-auto px-4 flex items-center justify-center">
                  <LazyVideo
                    ref={(el) => {
                      if (el) videoRefs.current[index] = el;
                    }}
                    src={video}
                    controls
                    loop
                    className="w-full h-auto max-h-[80vh] object-contain rounded-xl shadow-2xl bg-black"
                    style={{}}
                    onFocus={async (e) => {
                      try {
                        const playPromise = e.currentTarget.play();
                        if (playPromise) {
                          await playPromise;
                        }
                      } catch (error) {
                        // Ignore play errors (e.g., user interaction required)
                      }
                    }}
                    onMouseEnter={async (e) => {
                      try {
                        const playPromise = e.currentTarget.play();
                        if (playPromise) {
                          await playPromise;
                        }
                      } catch (error) {
                        // Ignore play errors
                      }
                    }}
                    onBlur={async (e) => {
                      try {
                        e.currentTarget.pause();
                        e.currentTarget.currentTime = 0;
                      } catch (error) {
                        // Ignore pause errors
                      }
                    }}
                    onMouseLeave={async (e) => {
                      try {
                        e.currentTarget.pause();
                        e.currentTarget.currentTime = 0;
                      } catch (error) {
                        // Ignore pause errors
                      }
                    }}
                    muted
                    playsInline
                  />
                </div>
              </div>
            ))}

            {videoEmbedUrl && !hasVideoFiles && (
              <div className="w-full bg-gray-50 py-12" style={{ display: 'block' }}>
                <div className="relative w-full max-w-6xl mx-auto px-4 flex items-center justify-center">
                  {videoEmbedUrl.includes('youtube.com') || videoEmbedUrl.includes('youtu.be') || videoEmbedUrl.includes('vimeo.com') ? (
                    <div className="w-full aspect-video rounded-xl overflow-hidden shadow-2xl">
                      <iframe
                        src={videoEmbedUrl}
                        className="w-full h-full"
                        style={{ border: 'none' }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={`Product Video Modal - ${product.title}`}
                      />
                    </div>
                  ) : (
                    <LazyVideo
                      ref={(el) => {
                        if (el) videoRefs.current['url'] = el;
                      }}
                      src={videoEmbedUrl}
                      controls
                      loop
                      className="w-full h-auto max-h-[80vh] object-contain rounded-xl shadow-2xl bg-black"
                      style={{}}
                      onFocus={async (e) => {
                        try {
                          const playPromise = e.currentTarget.play();
                          if (playPromise) {
                            await playPromise;
                          }
                        } catch (error) {
                          // Ignore play errors (e.g., user interaction required)
                        }
                      }}
                      onMouseEnter={async (e) => {
                        try {
                          const playPromise = e.currentTarget.play();
                          if (playPromise) {
                            await playPromise;
                          }
                        } catch (error) {
                          // Ignore play errors
                        }
                      }}
                      onBlur={async (e) => {
                        try {
                          e.currentTarget.pause();
                          e.currentTarget.currentTime = 0;
                        } catch (error) {
                          // Ignore pause errors
                        }
                      }}
                      onMouseLeave={async (e) => {
                        try {
                          e.currentTarget.pause();
                          e.currentTarget.currentTime = 0;
                        } catch (error) {
                          // Ignore pause errors
                        }
                      }}
                      muted
                      playsInline
                    />
                  )}
                </div>
              </div>
            )}

            {/* Banners - Full Screen */}
            {media.banners && media.banners.map((banner, index) => (
              <div
                key={index}
                className="w-full bg-black flex items-center justify-center"
                style={{ margin: 0, padding: 0, display: 'block', lineHeight: 0 }}
              >
                <img
                  src={banner}
                  alt={`Product banner ${index + 1}`}
                  className="w-full h-auto max-h-[500px] sm:max-h-[650px] lg:max-h-screen object-contain"
                  style={{ margin: 0, padding: 0, display: 'block', verticalAlign: 'top' }}
                  onError={(e) => {
                    // Fallback if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Specifications - Only if product has specifications */}
      {product.specifications && Object.keys(product.specifications).length > 0 && (
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-3xl mx-auto">
            <div id="specifications-section">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Specifications</h3>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <dl className="space-y-3">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-start py-2 border-b border-gray-200 last:border-0">
                      <dt className="text-gray-600 font-semibold pr-4">{key}:</dt>
                      <dd className="text-gray-900 text-right font-medium">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Related Products Section */}
      {relatedProducts.length > 0 && (
        <div className="w-full bg-gray-50 py-12 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Related Products</h3>
                <p className="text-sm text-gray-600">More products from <span className="capitalize font-medium">{product.category}</span></p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => scrollRelated('left')}
                  className="w-10 h-10 rounded-full bg-white shadow border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                  aria-label="Scroll left"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => scrollRelated('right')}
                  className="w-10 h-10 rounded-full bg-white shadow border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                  aria-label="Scroll right"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div
              ref={relatedScrollRef}
              className="flex space-x-6 overflow-x-auto pb-4 pt-1 scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {relatedProducts.map((relProduct) => (
                <div key={relProduct.id} className="w-[260px] sm:w-[280px] flex-shrink-0 flex flex-col">
                  <ProductCard product={relProduct} variant="compact" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full relative">
            <button
              onClick={() => {
                setShowVideoModal(false);
                setSelectedVideo(null);
              }}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <div className="aspect-video bg-black">
              <video
                src={selectedVideo}
                controls
                autoPlay
                preload="auto"
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      )}

      <PurchaseModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        product={product}
        selectedColorVariant={selectedColorVariant}
        onPurchase={handlePurchase}
      />
    </div>
  );
};

export default ProductDetailPage;
